import type { State } from '../configureStore'
import uuidv4 from 'uuid/v4'
import { Policies as ActionTypes } from '../actionTypes'
import type { Policy } from '../reducers/policies'
import * as ReduxHelpers from '@src/utils/reduxHelpers'
import { defaultHandleStatus } from '@src/endpoints/helpers'
import type { APIResponse } from '@src/endpoints'
import {
  PolicyReader,
  GetPoliciesResp,
  GetPoliciesReq
} from '@src/endpoints/services/policyReader.service'
import { parseCamURI, removePolicyPrefix } from '@src/utils/url.utils'
import { isEmpty } from 'lodash'

// Action Creators
export type ActionTypes =
  | getMonitoringListSuccessAction
  | setSelectedPolicySuccessAction
  | serverErrorsAction
  | requestPendingAction

export const getPoliciesSuccess = (policyList: Policy, isPending = false) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.GET_POLICIES_SUCCESS, {
    policyList,
    isPending
  })
type getMonitoringListSuccessAction = ReturnType<typeof getPoliciesSuccess>

const setSelectedPolicySuccess = (selectedPolicy: string, isPending = false) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.SET_SELECTED_POLICY, {
    selectedPolicy,
    isPending
  })
type setSelectedPolicySuccessAction = ReturnType<
  typeof setSelectedPolicySuccess
>

export const serverErrors = (errors: any) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.SERVER_ERRORS, {
    errors
  })
type serverErrorsAction = ReturnType<typeof serverErrors>

const requestPending = (isPending: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(ActionTypes.REQUEST_PENDING, {
    isPending
  })
type requestPendingAction = ReturnType<typeof requestPending>

export interface GetPoliciesReqEx extends GetPoliciesReq {
  identity_uri?: string
}

export function setSelectedPolicy(
  policy: string,
  accountIds: string[],
  identity_uri: string
) {
  return (dispatch: any, getState: () => State) => {
    const { policies } = getState()
    const { selectedPolicy } = policies
    if (selectedPolicy !== policy) {
      // for some policies we have URI or prefix that coming as policy
      // and looks like "InlinePolicy:policyName" or "GROUP:someGroup:policyName"
      // so we need left only policy name before passing to EP
      let policyName = parseCamURI(policy)?.id || policy
      policyName = removePolicyPrefix(policyName)
      dispatch(setSelectedPolicySuccess(policyName))
      dispatch(getPolicy(policyName, accountIds, identity_uri))
    }
  }
}

export function getPolicy(
  policy: string,
  accountIds: string[],
  identity_uri: string
) {
  return (dispatch: any, getState: () => State) => {
    if (!isEmpty(policy)) {
      dispatch(
        getPolicies({
          identity_uri,
          policies: [policy],
          account_ids: accountIds,
          actionError: serverErrors,
          actionPending: requestPending
        })
      )
    }
  }
}

interface GetPoliciesOptions {
  identity_uri: string
  policies?: string[]
  account_id?: string
  account_ids?: string[]
  actionSuccess?: <Type>(policies: Type[], isPending?: boolean) => void
  actionError?: (data?: string) => void
  actionPending?: (data?: boolean) => void
  showToastMessage?: boolean
  handleStatus?: (
    requestResult: APIResponse<GetPoliciesResp>,
    actionError?: (data?: string) => void,
    showToastMessage?: boolean,
    dispatch?: (data) => void
  ) => Promise<APIResponse<GetPoliciesResp>>
}

export function getPolicies({
  identity_uri,
  policies = [],
  account_ids = [],
  actionPending,
  actionError,
  actionSuccess = getPoliciesSuccess,
  showToastMessage = false,
  handleStatus = defaultHandleStatus
}: GetPoliciesOptions) {
  return (dispatch, getStore: () => State) => {
    if (actionPending) {
      dispatch(actionPending(true))
    }
    const params: GetPoliciesReqEx = {
      req_hdr: {
        tid: uuidv4()
      },
      policies,
      account_ids
    }
    if (identity_uri) {
      params.identity_uri = identity_uri
    }
    PolicyReader.GetPolicies({ params })
      .then(result =>
        handleStatus(result, actionError, showToastMessage, dispatch)
      )
      .then(result => {
        if (result.policies) {
          dispatch(actionSuccess(result.policies))
        }
        if (actionPending) {
          dispatch(actionPending(false))
        }
      })
      .catch(ex => ex)
  }
}
